<template>
  <EmployeeHorizontalMenu
    :menuItems="menuItems"
    :initialTab="activeLabel"
    @tab-selected="onTabSelected"
  />
</template>

<script setup>
import { ref, computed, watch, onMounted, getCurrentInstance } from "vue";
import EmployeeHorizontalMenu from "@components/EmployeeHorizontalMenu/EmployeeHorizontalMenu.vue";
import { getEmployeeBadges } from "@/api";
import {
  LD_FLAG_EMPLOYEE_POSITION_HISTORY_VIEW,
  LD_FLAG_IDEA_MDP_450,
  LD_FLAG_IDEA_MDP_609,
} from "@components/constants/launchDarklyFlagKeys.js";
import {
  shouldShowOrganizationEmployeeAssignmentsTab,
  shouldShowOrganizationEmployeeDashboardTab,
  shouldShowOrganizationEmployeeReadinessTabs,
} from "@/utils/organizationEmployeeDetailTabs";
import homeIcon from "@components/assets/icons/Home.svg";
import pathToReadinessIcon from "@components/assets/icons/path-to-rediness.svg";
import badgesIcon from "@components/assets/icons/badge.svg";
import positionHistoryIcon from "@components/assets/icons/position-history.svg";

const props = defineProps({
  employeeId: {
    type: [Number, String],
    default: null,
  },
  activeTab: {
    type: String,
    default: "",
  },
  accountDetails: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(["tab-selected", "tabs-ready"]);

const proxy = getCurrentInstance()?.proxy;

const showDashboardFlag = ref(false);
const showPositionHistoryFlag = ref(false);
const currentBadgesFlagEnabled = ref(false);
const badgesCount = ref(0);

const readinessOn = computed(() => shouldShowOrganizationEmployeeReadinessTabs(props.accountDetails));

// Order here is the order on screen.
const tabs = computed(() => [
  {
    key: "dashboard",
    label: "Dashboard",
    icon: homeIcon,
    visible: shouldShowOrganizationEmployeeDashboardTab(showDashboardFlag.value, props.accountDetails),
  },
  {
    key: "pathtoreadiness",
    label: "Path to Readiness",
    icon: pathToReadinessIcon,
    visible: readinessOn.value,
  },
  {
    key: "badges",
    label: "Badges",
    icon: badgesIcon,
    visible: readinessOn.value,
    badge: currentBadgesFlagEnabled.value && badgesCount.value > 0 ? badgesCount.value : undefined,
  },
  {
    key: "positionshistory",
    label: "Positions History",
    icon: positionHistoryIcon,
    visible: shouldShowOrganizationEmployeeAssignmentsTab(
      showPositionHistoryFlag.value,
      props.accountDetails
    ),
  },
]);

const visibleTabs = computed(() => tabs.value.filter((tab) => tab.visible));

const menuItems = computed(() =>
  visibleTabs.value.map(({ key, label, icon, badge }) => {
    const item = { key, label, icon, enabled: true };
    if (badge !== undefined) item.badge = badge;
    return item;
  })
);

const activeLabel = computed(
  () => visibleTabs.value.find((tab) => tab.key === props.activeTab)?.label || ""
);

function onTabSelected(label) {
  const tab = visibleTabs.value.find((item) => item.label === label);
  if (tab) emit("tab-selected", tab.key);
}

async function fetchCurrentBadgesCount() {
  try {
    const accountID = JSON.parse(localStorage.getItem("currentAccountID"));
    if (!accountID || !props.employeeId) return;

    const { data } = await getEmployeeBadges(accountID, props.employeeId, {
      status: "current",
      limit: 1,
    });
    badgesCount.value = data?.count ?? 0;
  } catch (error) {
    console.error("Failed to fetch current badges count", error);
  }
}

async function resolveFlag(key) {
  return (await proxy?.$getFlag?.(key, false)) === true;
}

watch(
  () => props.employeeId,
  (newId, oldId) => {
    if (newId === oldId) return;
    badgesCount.value = 0;
    if (currentBadgesFlagEnabled.value) fetchCurrentBadgesCount();
  }
);

// EmployeeBadgeHolderList dispatches employeeStore/currentEmployeeBadges after every
// issue/remove; re-fetch the authoritative server-side count so the tab stays fresh.
watch(
  () => proxy?.$store?.state?.employeeStore?.currentEmployeeBadgesCount,
  (newVal, oldVal) => {
    if (newVal !== oldVal && currentBadgesFlagEnabled.value) fetchCurrentBadgesCount();
  }
);

onMounted(async () => {
  [showPositionHistoryFlag.value, showDashboardFlag.value, currentBadgesFlagEnabled.value] =
    await Promise.all([
      resolveFlag(LD_FLAG_EMPLOYEE_POSITION_HISTORY_VIEW),
      resolveFlag(LD_FLAG_IDEA_MDP_609),
      resolveFlag(LD_FLAG_IDEA_MDP_450),
    ]);

  if (currentBadgesFlagEnabled.value) await fetchCurrentBadgesCount();

  emit(
    "tabs-ready",
    visibleTabs.value.map((tab) => tab.key)
  );
});
</script>
